import PDFMake from 'pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import store from '@/store/index.js'
import { groupBy, sumBy } from 'lodash'
import { numberWithCommas } from '@/utils/NumericMutations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { theme } from '@/plugins/theme'

PDFMake.vfs = pdfFonts.pdfMake.vfs

const DASH = '\u2014'

export async function generateScaleSheetPdf (ticket) {
  const scaleInfo = await store.dispatch('scale-data/fetchScaleDataForTicket', ticket.ticketId)
  const companyInfo = store.getters['user/companyInfo']

  const segmentsGrouped = groupBy(scaleInfo.segmentVolumes, s => s.logNumber)
  const grossVolume = sumBy(scaleInfo.segmentVolumes, sv => sv.grossVolume)
  const netVolume = sumBy(scaleInfo.segmentVolumes, sv => sv.netVolume)
  const deductionVolume = grossVolume - netVolume
  const pieces = Object.keys(segmentsGrouped).length

  const logs = Object.entries(segmentsGrouped).map(([logNumber, segments]) => ({
    logNumber: parseInt(logNumber),
    segments: segments.sort((a, b) => a.segmentNumber - b.segmentNumber)
  })).sort((a, b) => a.logNumber - b.logNumber)

  const companyName = companyInfo?.shortName || companyInfo?.name || ''
  const companyAddress = formatCompanyAddressInline(companyInfo)
  const ticketIdentifier = buildTicketIdentifier(ticket)

  const content = [
    getInfoSection(ticket, scaleInfo, grossVolume, deductionVolume, netVolume, pieces),
    getScaleDataLabel(),
    getLogTable(logs),
    getDisclaimer()
  ]

  const documentDefinition = {
    pageSize: 'LETTER',
    pageOrientation: 'portrait',
    pageMargins: [28, 52, 28, 36],
    header: () => getHeaderSection(companyName, companyAddress, ticketIdentifier),
    content,
    styles: getStyles(),
    defaultStyle: { fontSize: 7 },
    footer: (currentPage) => getScaleSheetFooter(currentPage, companyName)
  }

  const fileName = `scalesheet_${ticket.ticketNumber}_${companyName.replace(/\s+/g, '')}`
  PDFMake.createPdf(documentDefinition).download(fileName)
}

function buildTicketIdentifier (ticket) {
  let identifier = `Ticket # ${ticket.ticketNumber}`
  if (ticket.extTicketNumber1) identifier += ` \u00B7 Ext ${ticket.extTicketNumber1}`
  if (ticket.extTicketNumber2) identifier += ` \u00B7 Ext2 ${ticket.extTicketNumber2}`
  if (ticket.trailerIdentifier) identifier += ` \u00B7 Trailer ${ticket.trailerIdentifier}`
  return identifier
}

function formatCompanyAddressInline (companyInfo) {
  if (companyInfo?.address?.addressLine1 && companyInfo?.address?.city && companyInfo?.address?.state && companyInfo?.address?.postalCode) {
    return `${companyInfo.address.addressLine1} \u00B7 ${companyInfo.address.city}, ${companyInfo.address.state} ${companyInfo.address.postalCode}`
  }
  return ''
}

function getHeaderSection (companyName, companyAddress, ticketIdentifier) {
  return {
    margin: [28, 12, 28, 0],
    table: {
      widths: ['*', 'auto'],
      body: [
        [
          { text: `${companyName} Scale Sheet`, style: 'headerTitle', border: [false, false, false, false] },
          { text: '', border: [false, false, false, false] }
        ],
        [
          { text: companyAddress, style: 'headerSubtitle', border: [false, false, false, true] },
          { text: ticketIdentifier, style: 'headerSubtitle', alignment: 'right', border: [false, false, false, true] }
        ]
      ]
    },
    layout: {
      hLineWidth: (i, node) => (i === node.table.body.length) ? 1 : 0,
      hLineColor: () => '#999999',
      vLineWidth: () => 0,
      paddingBottom: () => 2,
      paddingTop: () => 1
    }
  }
}

function getInfoSection (ticket, scaleInfo, grossVolume, deductionVolume, netVolume, pieces) {
  const weighedIn = ticket.weighedInAt ? utcToLocalDate(ticket.weighedInAt, 'MMM D, YYYY h:mm A') : 'N/A'
  const weighedOut = ticket.weighedOutAt ? utcToLocalDate(ticket.weighedOutAt, 'MMM D, YYYY h:mm A') : 'N/A'
  const inWeight = ticket.inWeight ? `${numberWithCommas(ticket.inWeight)} lbs` : 'N/A'
  const outWeight = ticket.outWeight ? `${numberWithCommas(ticket.outWeight)} lbs` : 'N/A'
  const netWeight = (ticket.inWeight && ticket.outWeight) ? `${numberWithCommas(ticket.inWeight - ticket.outWeight)} lbs` : 'N/A'

  const leftRows = [
    [{ text: 'Tract', style: 'infoLabel' }, { text: ticket.tract || 'N/A', style: 'infoValue' }]
  ]
  if (ticket.logger) {
    leftRows.push([{ text: '', style: 'infoLabel' }, { text: `${ticket.logger} Feller`, style: 'infoValueSmall' }])
  }
  leftRows.push(
    [{ text: 'Account', style: 'infoLabel' }, { text: ticket.account || 'N/A', style: 'infoValue' }],
    [{ text: 'Destination', style: 'infoLabel' }, { text: ticket.destination || 'N/A', style: 'infoValue' }],
    [{ text: 'Setting', style: 'infoLabel' }, { text: ticket.setting || 'N/A', style: 'infoValue' }],
    [{ text: 'Product', style: 'infoLabel' }, { text: ticket.product || 'N/A', style: 'infoValue' }],
    [{ text: 'Scaler', style: 'infoLabel' }, { text: scaleInfo.scaledByUser || 'N/A', style: 'infoValue' }]
  )

  const infoLayout = {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 4,
    paddingRight: () => 4,
    paddingTop: () => 2,
    paddingBottom: () => 2,
    fillColor: (rowIndex) => rowIndex % 2 !== 0 ? '#F4F4F4' : null
  }

  return {
    margin: [0, 0, 0, 6],
    columns: [
      {
        width: '*',
        table: { widths: ['auto', '*'], body: leftRows },
        layout: infoLayout
      },
      {
        width: 'auto',
        table: {
          widths: ['auto', 'auto'],
          body: [
            [{ text: 'Weighed In', style: 'infoLabel' }, { text: weighedIn, style: 'infoValue' }],
            [{ text: 'Weighed Out At', style: 'infoLabel' }, { text: weighedOut, style: 'infoValue' }],
            [{ text: 'In Weight', style: 'infoLabel' }, { text: inWeight, style: 'infoValue', alignment: 'right' }],
            [{ text: 'Out Weight', style: 'infoLabel' }, { text: outWeight, style: 'infoValue', alignment: 'right' }],
            [{ text: 'Net Weight', style: 'infoLabel' }, { text: netWeight, style: 'infoValue', alignment: 'right' }]
          ]
        },
        layout: infoLayout
      },
      {
        width: 'auto',
        table: {
          widths: ['auto', 'auto'],
          body: [
            [{ text: 'Gross Volume (BF)', style: 'infoLabel' }, { text: numberWithCommas(grossVolume), style: 'infoValueBold', alignment: 'right' }],
            [{ text: 'Deduction Volume (BF)', style: 'infoLabel' }, { text: numberWithCommas(deductionVolume), style: 'infoValueBold', alignment: 'right' }],
            [{ text: 'Net Volume (BF)', style: 'infoLabel' }, { text: numberWithCommas(netVolume), style: 'infoValueBold', alignment: 'right' }],
            [{ text: 'Pieces', style: 'infoLabel' }, { text: pieces.toString(), style: 'infoValueBold', alignment: 'right' }]
          ]
        },
        layout: infoLayout
      }
    ],
    columnGap: 10
  }
}

function getScaleDataLabel () {
  return {
    text: 'SCALE DATA BY LOG',
    style: 'sectionTitle',
    margin: [0, 0, 0, 2]
  }
}

function getLogTable (logs) {
  const headerRow = [
    { text: '#', style: 'tableHeader', alignment: 'right' },
    { text: 'Species', style: 'tableHeader', alignment: 'left' },
    { text: 'Len (ft)', style: 'tableHeader', alignment: 'right' },
    { text: 'Dia (in)', style: 'tableHeader', alignment: 'right' },
    { text: 'Butt', style: 'tableHeader', alignment: 'right' },
    { text: 'Gross Vol (BF)', style: 'tableHeader', alignment: 'right' },
    { text: 'Ded Vol (BF)', style: 'tableHeader', alignment: 'right' },
    { text: 'Net Vol (BF)', style: 'tableHeader', alignment: 'right' },
    { text: 'Grade', style: 'tableHeader', alignment: 'left' },
    { text: 'Seg', style: 'tableHeader', alignment: 'right' },
    { text: 'Len (ft)', style: 'tableHeader', alignment: 'right' },
    { text: 'Top (in)', style: 'tableHeader', alignment: 'right' },
    { text: 'Gross Vol (BF)', style: 'tableHeader', alignment: 'right' },
    { text: 'Ded Vol (BF)', style: 'tableHeader', alignment: 'right' },
    { text: 'Net Vol (BF)', style: 'tableHeader', alignment: 'right' }
  ]

  const body = [headerRow]
  const rowLogIndex = []

  for (let logIdx = 0; logIdx < logs.length; logIdx++) {
    const log = logs[logIdx]
    const segments = log.segments
    const totalLength = sumBy(segments, s => s.length)
    const logGrossVolume = sumBy(segments, s => s.grossVolume)
    const logDeductionVolume = sumBy(segments, s => s.grossVolume - s.netVolume)
    const logNetVolume = sumBy(segments, s => s.netVolume)
    const hasButtCut = segments.some(s => s.buttLog)
    const topDiameter = segments[0].diameter.toString()

    const logSummaryCells = [
      { text: log.logNumber.toString(), style: 'tableCell', alignment: 'right' },
      { text: segments[0].species || '', style: 'tableCell', alignment: 'left' },
      { text: totalLength.toString(), style: 'tableCell', alignment: 'right' },
      { text: topDiameter, style: 'tableCell', alignment: 'right' },
      { text: hasButtCut ? 'X' : '', style: 'tableCell', alignment: 'right' },
      { text: numberWithCommas(logGrossVolume), style: 'tableCell', alignment: 'right' },
      { text: logDeductionVolume > 0 ? numberWithCommas(logDeductionVolume) : DASH, style: 'tableCell', alignment: 'right' },
      { text: numberWithCommas(logNetVolume), style: 'tableCell', alignment: 'right' },
      { text: segments[0].grade || '', style: 'tableCell', alignment: 'left' }
    ]

    if (segments.length === 1) {
      body.push([...logSummaryCells, ...buildSegmentCells(segments[0])])
      rowLogIndex.push(logIdx)
    } else {
      // All segments stacked within a single row so dontBreakRows keeps the log together
      const centerMargin = Math.round((segments.length - 1) * 5)
      const centeredLogCells = logSummaryCells.map(cell => ({
        ...cell,
        margin: [0, centerMargin, 0, 0]
      }))
      const segDeductionFn = (s) => {
        const d = s.grossVolume - s.netVolume
        return d > 0 ? numberWithCommas(d) : DASH
      }
      const segStackCells = [
        buildSegmentStack(segments, s => s.segmentNumber.toString(), 'right'),
        buildSegmentStack(segments, s => s.length.toString(), 'right'),
        buildSegmentStack(segments, s => s.diameter.toString(), 'right'),
        buildSegmentStack(segments, s => numberWithCommas(s.grossVolume), 'right'),
        buildSegmentStack(segments, segDeductionFn, 'right'),
        buildSegmentStack(segments, s => numberWithCommas(s.netVolume), 'right')
      ]
      body.push([...centeredLogCells, ...segStackCells])
      rowLogIndex.push(logIdx)
    }
  }

  return {
    table: {
      headerRows: 1,
      dontBreakRows: true,
      widths: [
        'auto', 'auto', 'auto', 'auto', 'auto',
        'auto', 'auto', 'auto', '*',
        'auto', 'auto', 'auto', 'auto', 'auto', 'auto'
      ],
      body
    },
    layout: {
      hLineWidth: (i, node) => {
        if (i === 0 || i === node.table.body.length) return 0.75
        if (i === 1) return 0.75
        return 0.5
      },
      vLineWidth: (i, node) => {
        if (i === 0 || i === node.table.widths.length) return 0.75
        if (i === 9) return 0.75
        return 0.5
      },
      hLineColor: (i, node) => {
        if (i === 0 || i === 1 || i === node.table.body.length) return '#2D2D2D'
        return '#AAAAAA'
      },
      vLineColor: (i, node) => {
        if (i === 0 || i === node.table.widths.length || i === 9) return '#2D2D2D'
        return '#AAAAAA'
      },
      paddingLeft: () => 3,
      paddingRight: () => 3,
      paddingTop: () => 2,
      paddingBottom: () => 2,
      fillColor: (rowIndex) => {
        if (rowIndex === 0) return '#E8E8E8'
        const logIdx = rowLogIndex[rowIndex - 1]
        return logIdx % 2 === 0 ? '#F4F4F4' : null
      }
    }
  }
}

function buildSegmentCells (seg) {
  const segDeduction = seg.grossVolume - seg.netVolume
  return [
    { text: seg.segmentNumber.toString(), style: 'tableCell', alignment: 'right' },
    { text: seg.length.toString(), style: 'tableCell', alignment: 'right' },
    { text: seg.diameter.toString(), style: 'tableCell', alignment: 'right' },
    { text: numberWithCommas(seg.grossVolume), style: 'tableCell', alignment: 'right' },
    { text: segDeduction > 0 ? numberWithCommas(segDeduction) : DASH, style: 'tableCell', alignment: 'right' },
    { text: numberWithCommas(seg.netVolume), style: 'tableCell', alignment: 'right' }
  ]
}

function buildSegmentStack (segments, getValueFn, alignment) {
  return {
    table: {
      widths: ['*'],
      body: segments.map(seg => [{
        text: getValueFn(seg),
        fontSize: 6.5,
        alignment: alignment || 'right'
      }])
    },
    layout: {
      hLineWidth: (i, node) => (i > 0 && i < node.table.body.length) ? 0.5 : 0,
      hLineColor: () => '#AAAAAA',
      vLineWidth: () => 0,
      paddingLeft: () => 3,
      paddingRight: () => 3,
      paddingTop: () => 1,
      paddingBottom: () => 1
    },
    margin: [-3, 0, -3, 0]
  }
}

function getDisclaimer () {
  return {
    text: 'This document is for informational purposes only and does not represent a financial settlement.',
    style: 'disclaimer',
    margin: [0, 8, 0, 0]
  }
}

function getScaleSheetFooter (currentPage, companyName) {
  return {
    margin: [28, 0, 28, 0],
    columns: [
      {
        text: `Generated by Angel \u00B7 ${companyName} \u00B7 Page ${currentPage}`,
        fontSize: 7,
        color: '#888888',
        alignment: 'center'
      }
    ]
  }
}

function getStyles () {
  return {
    headerTitle: {
      fontSize: 11,
      bold: true,
      color: theme.secondary
    },
    headerSubtitle: {
      fontSize: 7,
      color: '#555555'
    },
    infoLabel: {
      fontSize: 7,
      bold: true,
      margin: [0, 1, 6, 1]
    },
    infoValue: {
      fontSize: 7,
      margin: [0, 1, 0, 1]
    },
    infoValueSmall: {
      fontSize: 6.5,
      margin: [0, 0, 0, 1],
      color: '#555555'
    },
    infoValueBold: {
      fontSize: 7.5,
      bold: true,
      margin: [0, 1, 0, 1]
    },
    sectionTitle: {
      fontSize: 8,
      bold: true,
      color: theme.secondary
    },
    tableHeader: {
      fontSize: 6,
      bold: true,
      color: '#333333'
    },
    tableCell: {
      fontSize: 6.5
    },
    disclaimer: {
      fontSize: 7,
      italics: true,
      color: '#888888',
      alignment: 'center'
    }
  }
}
